import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false)
  const navigate = useNavigate()

  const handleAboutUsClick = () => {
    if (window.location.pathname === '/') {
      const element = document.getElementById('our-heritage')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      navigate('/#our-heritage')
    }
  }

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Cleanup function to reset body overflow when component unmounts
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  const memberServicesDropdown = [
    { name: 'Aesthetics & Anti-Aging', href: '/member-services/aesthetics-anti-aging' },
    { name: 'Art Escrow & Consulting', href: '/member-services/art-escrow-consulting' },
    { name: 'Black Car Service', href: '/member-services/black-car-service' },
    { name: 'Education Consulting', href: '/member-services/education-consulting' },
    { name: 'Invite-Only Lounge', href: '/member-services/invite-only-lounge' },
    { name: 'Personal Travel Planner', href: '/member-services/personal-travel-planner' },
    { name: 'Private Dining', href: '/member-services/private-dining' },
    { name: 'Private Jet Charter', href: '/member-services/private-jet-charter' },
    { name: 'Ticketing & Events', href: '/member-services/ticketing-events' },
    { name: 'Yacht Charter', href: '/member-services/yacht-charter' },
  ]

  const bespokeShoppingDropdown = [
    { name: 'Artwork', href: '/bespoke-shopping/artwork' },
    { name: 'Cigars', href: '/bespoke-shopping/cigars' },
    { name: 'Diamonds', href: '/bespoke-shopping/diamonds' },
    { name: 'Estates', href: '/bespoke-shopping/estates' },
    { name: 'Fine Wine', href: '/bespoke-shopping/fine-wine' },
    { name: 'Moutai', href: '/bespoke-shopping/moutai' },
    { name: 'Watches', href: '/bespoke-shopping/watches' },
    { name: 'Whiskey', href: '/bespoke-shopping/whiskey' },
  ]

  const leftNavigation = [
    { name: 'About Us', href: '/#our-heritage' },
    {
      name: 'Member Services',
      href: '/member-services',
      hasDropdown: true,
      dropdown: memberServicesDropdown
    },
    {
      name: 'Bespoke Shopping',
      href: '/bespoke-shopping',
      hasDropdown: true,
      dropdown: bespokeShoppingDropdown
    },
    { name: 'Editorial', href: '/editorial' },
  ]

  const handleMouseEnter = (itemName: string) => {
    setActiveDropdown(itemName)
  }

  const handleMouseLeave = () => {
    setActiveDropdown(null)
  }

  const handleLanguageMouseLeave = () => {
    setLanguageDropdownOpen(false)
  }

  return (
    <header
      className={`w-full ${isMenuOpen ? 'fixed top-0 z-50' : 'sticky top-0 z-50'} px-4 md:px-16 py-4`}
      style={{
        backgroundColor: '#000000'
      }}
    >
      <div className="flex items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src="/Logo/Logo_White.png"
            alt="FGA Prestige Logo"
            width={148}
            height={36}
            style={{ width: 148, height: 36 }}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav
          className="hidden md:flex items-center"
          style={{ marginLeft: '64px' }}
        >
          {leftNavigation.map((item, index) => (
            <div
              key={item.name}
              className="relative"
              onMouseEnter={() => item.hasDropdown && handleMouseEnter(item.name)}
              onMouseLeave={handleMouseLeave}
              style={{
                marginRight: index < leftNavigation.length - 1 ? '24px' : '0',
              }}
            >
              {item.name === 'About Us' ? (
                <div
                  onClick={handleAboutUsClick}
                  className="hover:opacity-80 transition-opacity duration-300 cursor-pointer"
                  style={{
                    color: '#FFFFFF',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '16px',
                    textDecoration: 'none'
                  }}
                >
                  {item.name}
                </div>
              ) : (
                <Link
                  to={item.href}
                  className="hover:opacity-80 transition-opacity duration-300"
                  style={{
                    color: '#FFFFFF',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '16px',
                    textDecoration: 'none'
                  }}
                >
                  {item.name}
                </Link>
              )}

              {/* Dropdown Menu */}
              {item.hasDropdown && activeDropdown === item.name && (
                <div
                  className="absolute top-full left-0 z-50"
                  style={{
                    backgroundColor: '#000000',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    paddingTop: '16px',
                    paddingBottom: '16px',
                    width: '220px',
                    maxWidth: 'calc(100vw - 30px)' // Prevent overlap with scroll bar
                  }}
                >
                  {item.dropdown?.map((dropdownItem, dropdownIndex) => (
                    <Link
                      key={dropdownItem.name}
                      to={dropdownItem.href}
                      className="block transition-colors duration-200"
                      style={{
                        color: '#FFFFFF',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        fontSize: '14px',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        marginBottom: dropdownIndex < (item.dropdown?.length || 0) - 1 ? '8px' : '0'
                      }}
                      onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        e.currentTarget.style.color = '#BDA36C'
                      }}
                      onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        e.currentTarget.style.color = '#FFFFFF'
                      }}
                    >
                      {dropdownItem.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Right Navigation */}
        <nav className="hidden md:flex items-center ml-auto">
          <Link
            to="/contact"
            className="hover:opacity-80 transition-opacity duration-300 mr-4"
            style={{
              color: '#FFFFFF',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              textDecoration: 'none'
            }}
          >
            Contact Us
          </Link>
        </nav>
        {/* Language Selector */}
        <div
          className="hidden md:flex items-center ml-4 relative"
          onMouseLeave={handleLanguageMouseLeave}
        >
          <button
            onMouseEnter={() => setLanguageDropdownOpen(true)}
            className="hover:opacity-80 transition-opacity duration-300"
            style={{
              color: '#FFFFFF',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            English
          </button>

          {/* Language Dropdown */}
          {languageDropdownOpen && (
            <div
              className="absolute top-full left-0 z-50"
              style={{
                backgroundColor: '#000000',
                borderRadius: '4px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                paddingTop: '8px',
                paddingBottom: '8px',
                width: '80px'
              }}
            >
              <button
                className="block w-full text-left transition-colors duration-200"
                style={{
                  color: '#FFFFFF',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  paddingTop: '8px',
                  paddingBottom: '8px'
                }}
                onClick={() => setLanguageDropdownOpen(false)}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.color = '#BDA36C'
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.color = '#FFFFFF'
                }}
              >
                繁體
              </button>
              <button
                className="block w-full text-left transition-colors duration-200"
                style={{
                  color: '#FFFFFF',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  paddingTop: '8px',
                  paddingBottom: '8px'
                }}
                onClick={() => setLanguageDropdownOpen(false)}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.color = '#BDA36C'
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.currentTarget.style.color = '#FFFFFF'
                }}
              >
                简体
              </button>
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden ml-auto flex items-center justify-center w-8 h-8"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#FFFFFF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <div className="flex flex-col space-y-1">
              <span
                className="w-6 h-0.5 transition-all duration-300"
                style={{ backgroundColor: '#FFFFFF' }}
              />
              <span
                className="w-6 h-0.5 transition-all duration-300"
                style={{ backgroundColor: '#FFFFFF' }}
              />
              <span
                className="w-6 h-0.5 transition-all duration-300"
                style={{ backgroundColor: '#FFFFFF' }}
              />
            </div>
          )}
        </button>
      </div>

      {/* Full-Screen Mobile Navigation Overlay */}
      {isMenuOpen && (
        <div
          className="fixed top-16 left-0 right-0 bottom-0 z-40 md:hidden overflow-y-auto"
          style={{
            backgroundColor: '#000000',
            paddingTop: '32px'
          }}
        >
          {/* Mobile Navigation Menu - Scrollable Content */}
          <div
            className="min-h-screen flex flex-col"
            style={{
              paddingLeft: '24px',
              paddingRight: '24px',
              paddingTop: '32px',
              paddingBottom: '120px' // Space for language selector
            }}
          >
            {/* About Us */}
            <Link
              to="/#our-heritage"
              onClick={() => setIsMenuOpen(false)}
              className="block transition-opacity duration-300 hover:opacity-80"
              style={{
                color: '#FFFFFF',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '18px',
                fontWeight: '400',
                textDecoration: 'none',
                marginBottom: '32px'
              }}
            >
              About Us
            </Link>

            {/* Member Services */}
            <div style={{ marginBottom: '32px' }}>
              <Link
                to="/member-services"
                onClick={() => setIsMenuOpen(false)}
                className="block transition-opacity duration-300 hover:opacity-80"
                style={{
                  color: '#FFFFFF',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '18px',
                  fontWeight: '400',
                  textDecoration: 'none',
                  marginBottom: '16px'
                }}
              >
                Member Services
              </Link>
              <div style={{ paddingLeft: '0px' }}>
                {memberServicesDropdown.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="block transition-opacity duration-300 hover:opacity-80"
                    style={{
                      color: '#FFFFFF',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '16px',
                      fontWeight: '300',
                      textDecoration: 'none',
                      marginBottom: '12px',
                      opacity: '0.9'
                    }}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Bespoke Shopping */}
            <div style={{ marginBottom: '32px' }}>
              <Link
                to="/bespoke-shopping"
                onClick={() => setIsMenuOpen(false)}
                className="block transition-opacity duration-300 hover:opacity-80"
                style={{
                  color: '#FFFFFF',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '18px',
                  fontWeight: '400',
                  textDecoration: 'none',
                  marginBottom: '16px'
                }}
              >
                Bespoke Shopping
              </Link>
              <div style={{ paddingLeft: '0px' }}>
                {bespokeShoppingDropdown.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="block transition-opacity duration-300 hover:opacity-80"
                    style={{
                      color: '#FFFFFF',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '16px',
                      fontWeight: '300',
                      textDecoration: 'none',
                      marginBottom: '12px',
                      opacity: '0.9'
                    }}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Editorial */}
            <Link
              to="/editorial"
              onClick={() => setIsMenuOpen(false)}
              className="block transition-opacity duration-300 hover:opacity-80"
              style={{
                color: '#FFFFFF',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '18px',
                fontWeight: '400',
                textDecoration: 'none',
                marginBottom: '32px'
              }}
            >
              Editorial
            </Link>

            {/* Contact Us */}
            <Link
              to="/contact"
              onClick={() => setIsMenuOpen(false)}
              className="block transition-opacity duration-300 hover:opacity-80"
              style={{
                color: '#FFFFFF',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '18px',
                fontWeight: '400',
                textDecoration: 'none',
                marginBottom: '32px'
              }}
            >
              Contact Us
            </Link>

            {/* Language Selector */}
            <div
              className="mt-auto"
              style={{
                paddingTop: '32px',
                paddingBottom: '32px'
              }}
            >
              <div className="flex items-center space-x-4">
                <button
                  className="transition-opacity duration-300 hover:opacity-80"
                  style={{
                    color: '#FFFFFF',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '16px',
                    fontWeight: '400',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  English
                </button>
                <span style={{ color: '#FFFFFF', fontSize: '16px' }}>|</span>
                <button
                  className="transition-opacity duration-300 hover:opacity-80"
                  style={{
                    color: '#FFFFFF',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '16px',
                    fontWeight: '400',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  繁體
                </button>
                <span style={{ color: '#FFFFFF', fontSize: '16px' }}>|</span>
                <button
                  className="transition-opacity duration-300 hover:opacity-80"
                  style={{
                    color: '#FFFFFF',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '16px',
                    fontWeight: '400',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  简体
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

export default Header